import React, { memo } from "react";
import { Box, type BoxProps } from "@chakra-ui/react";

import type { PublicCardTone } from "./PublicCard";

export type PublicIconMarkShape = "circle" | "softSquare" | "diamond";
export type PublicIconMarkSize = "sm" | "md" | "lg";

export type PublicIconMarkProps = Omit<BoxProps, "children"> & {
  children: React.ReactNode;
  tone?: PublicCardTone;
  shape?: PublicIconMarkShape;
  size?: PublicIconMarkSize;
};

const PUBLIC_ICON_TONES = {
  neutral: {
    bg: "moveon.publicFont",
    color: "moveon.public.surface.base",
  },
  primary: {
    bg: "moveon.primary",
    color: "moveon.public.surface.base",
  },
  secondary: {
    bg: "moveon.secondary",
    color: "moveon.public.surface.base",
  },
  tertiary: {
    bg: "moveon.tertiary",
    color: "moveon.public.surface.base",
  },
  success: {
    bg: "moveon.success",
    color: "moveon.public.surface.base",
  },
  warning: {
    bg: "moveon.warning",
    color: "moveon.public.surface.base",
  },
  danger: {
    bg: "moveon.danger",
    color: "moveon.public.surface.base",
  },
} as const;

const PUBLIC_ICON_SIZES = {
  sm: {
    box: "9",
    icon: "4",
  },
  md: {
    box: "12",
    icon: "5",
  },
  lg: {
    box: "14",
    icon: "6",
  },
} as const;

const PUBLIC_ICON_SHAPES = {
  circle: {
    rounded: "full",
    transform: undefined,
  },
  softSquare: {
    rounded: "xl",
    transform: undefined,
  },
  diamond: {
    rounded: "lg",
    transform: "rotate(45deg)",
  },
} as const;

function PublicIconMark({
  children,
  tone = "primary",
  shape = "circle",
  size = "md",
  ...boxProps
}: PublicIconMarkProps) {
  const toneProps = PUBLIC_ICON_TONES[tone];
  const sizeProps = PUBLIC_ICON_SIZES[size];
  const shapeProps = PUBLIC_ICON_SHAPES[shape];
  const contentTransform = shape === "diamond" ? "rotate(-45deg)" : undefined;

  return (
    <Box
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      h={sizeProps.box}
      w={sizeProps.box}
      flexShrink="0"
      {...toneProps}
      {...shapeProps}
      {...boxProps}
    >
      <Box
        as="span"
        display="inline-flex"
        alignItems="center"
        justifyContent="center"
        boxSize={sizeProps.icon}
        transform={contentTransform}
      >
        {children}
      </Box>
    </Box>
  );
}

export default memo(PublicIconMark);
